/**
 * External dependencies
 */
import debugFactory from "debug";
import page from "page";
import url from "url";
import { parse } from "qs";
import { once } from "lodash";
import React from "react";

/**
 * Internal dependencies
 */
import { setupRoutes } from "sections-middleware";
import { setRoute as setRouteAction } from "state/ui/actions";
import userFactory from "lib/user";
import { hydrate } from "lib/controller/utils";
import { fetchPreferences } from "state/preferences/actions";
import Preloader from "components/preloader";

//new
import { createReduxStore } from "state";
import initialReducer from "state/reducer";
import { setCurrentUser } from "state/current-user/actions";
import { requestDashboardLayouts } from "state/dashboard-layouts/actions";
import { requestReports } from "state/reports/actions";
import { initializeDashboard } from "state/dashboard/actions";
import { requestGraphCubeMeta } from "state/graph-cube/actions";
import { getPreference } from "state/preferences/selectors";
import { setDateRange } from "state/ui/date-range/actions";
import { setupLoaded } from "state/startup/actions";
import { isLoaded } from "state/startup/selectors";

const debug = debugFactory("makerpos");

const setupContextMiddleware = (reduxStore) => {
  page("*", (context, next) => {
    // page.js url parsing is broken so we had to disable it with `decodeURLComponents: false`
    const parsed = url.parse(context.canonicalPath, true);
    context.prevPath = parsed.path === context.path ? false : parsed.path;
    context.query = parsed.query;

    context.hashstring = (parsed.hash && parsed.hash.substring(1)) || "";
    // set `context.hash` (we have to parse manually)
    if (context.hashstring) {
      try {
        context.hash = parse(context.hashstring);
      } catch (e) {
        debug("failed to query-string parse `location.hash`", e);
        context.hash = {};
      }
    } else {
      context.hash = {};
    }

    context.store = reduxStore;

    // client version of the isomorphic method for redirecting to another page
    context.redirect = (httpCode, newUrl = null) => {
      if (isNaN(httpCode) && !newUrl) {
        newUrl = httpCode;
      }

      return page.replace(newUrl, context.state, false, false);
    };

    next();
  });

  page.exit("*", (context, next) => {
    if (!context.store) {
      context.store = reduxStore;
    }
    next();
  });
};

const onceFetchPreferences = async (reduxStore) => {
  await reduxStore.dispatch(requestGraphCubeMeta());
  await reduxStore.dispatch(fetchPreferences());
  await reduxStore.dispatch(requestDashboardLayouts());
  await reduxStore.dispatch(requestReports());
};

const onceInitializeDashboard = once(async (reduxStore) => {
  reduxStore.dispatch(initializeDashboard());
});

const loggedInMiddleware = async (currentUser, reduxStore) => {
  if (!currentUser.get()) {
    return;
  }

  page("/logout", () => {
    currentUser.clear();
    return (window.location = "/login");
  });

  page("*", async (context, next) => {
    if (isLoaded(context.store.getState()) === false) {
      let context = {};
      // this causes a DIV tag mismatch warning
      context.layout = <Preloader />;
      hydrate(context);
      try {
        await onceFetchPreferences(reduxStore);
        await onceInitializeDashboard(reduxStore);
        await dateTimeSetupMiddleware(currentUser, reduxStore);
        reduxStore.dispatch(setupLoaded());
      } catch (error) {
        if (error.message == "Forbidden") {
          currentUser.clear();
          return (window.location = "/login");
        }
      }
    }
    next();
  });
};

const dateTimeSetupMiddleware = (currentUser, reduxStore) => {
  if (!currentUser.get()) {
    return;
  }

  const state = reduxStore.getState();
  const userPreference = getPreference(state, "dateRange") || "This month";
  reduxStore.dispatch(setDateRange(userPreference));
};

const loadSectionsMiddleware = () => setupRoutes();

// do: check if the route is white listed
// hint: look at the routes in section
// if its not there, do something
const whitelistedSectionMiddleware = () => null;

// if the user is not logged in
// then some routes should not be accessible
// hint: make all routes not acceessible and add prop to skip
const loggedOutMiddleware = (currentUser) => {
  if (currentUser.get() === null) {
    const currentQueryParams = window.location.search;
    page.redirect(`/login${currentQueryParams}`);
  }
};

const setRouteMiddleware = () => {
  page("*", (context, next) => {
    context.store.dispatch(setRouteAction(context.pathname, context.query));
    next();
  });
};

export const configureReduxStore = (reduxStore, currentUser) => {
  debug("Executing configure Redux store.");
  if (currentUser.get()) {
    reduxStore.dispatch(setCurrentUser(currentUser.get()));
  }
};

/**
 * [description]
 *
 * @param  {[type]} reduxStore [description]
 * @return {[type]}            [description]
 */
export const setupMiddlewares = (currentUser, reduxStore) => {
  debug("Executing setup middlewares.");
  setupContextMiddleware(reduxStore);
  loadSectionsMiddleware();
  whitelistedSectionMiddleware();
  loggedOutMiddleware(currentUser);
  loggedInMiddleware(currentUser, reduxStore);
  setRouteMiddleware();
};

const boot = (currentUser) => {
  const reduxStore = createReduxStore({}, initialReducer);
  configureReduxStore(reduxStore, currentUser);
  setupMiddlewares(currentUser, reduxStore);
  page.start({ decodeURLComponents: false });
};

export const bootApp = (appName) => {
  const user = userFactory();
  user.initialize().then(() => {
    debug(`Starting ${appName}`);
    boot(user);
  });
};
